import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Bell,
  Building2,
  CalendarCheck,
  FileUp,
  Globe,
  Laptop,
  MapPin,
  Megaphone,
  Monitor,
  Moon,
  Palette,
  Plug,
  Plus,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Sun,
  Trash2,
  Upload,
  User,
  type LucideIcon,
} from 'lucide-react';
import { type FormEvent, useEffect, useState, useId } from 'react';
import {
  type ImportResult,
  unmappedQueryOptions,
  useImportAttendance,
  useLinkEmployee,
  useRemoveUnmapped,
} from '~/api/attendance';
import {
  sessionsQueryOptions,
  useChangePassword,
  useLogout,
  useMe,
  useRevokeSession,
} from '~/api/auth';
import { formError, inlineError } from '~/api/client';
import { peopleQueryOptions, useRemoveAvatar, useSetAvatar } from '~/api/people';
import { clockSkewSeconds } from '~/api/serverClock';
import { CompanyRoles } from '~/features/orders/Roles';
import { CoverageCalendar } from '~/features/attendance/CoverageCalendar';
import { AvatarCropper } from '~/features/people/AvatarCropper';
import { OfficeSitesSection } from '~/features/clock/OfficeSites';
import { ConnectedApps } from '~/features/meetings/ConnectedApps';
import { ClaudeSection } from '~/features/settings/ClaudeSection';
import { NotificationsSettings } from '~/features/notifications/NotificationsSettings';
import { SendNotification } from '~/features/notifications/SendNotification';
import { DatePicker } from '~/ui/DatePicker';
import {
  holidaysQueryOptions,
  settingsQueryOptions,
  type UpdateSettingsBody,
  type WorkspaceView,
  useCreateHoliday,
  useDeleteHoliday,
  useUpdateSettings,
} from '~/api/workspace';
import { useWeekStart } from '~/lib/appearance';
import {
  describeSchedule,
  formatDateTime,
  relativeTime,
  ROLE_LABEL,
  STATUS_LABEL,
} from '~/lib/format';
import { notify } from '~/lib/notify';
import { systemTheme, type Theme, useTheme } from '~/lib/theme';
import { ChatLookSettings } from '~/features/team-chat/AppearancePane';
import { ThemeEditor } from '~/features/appearance/ThemeEditor';
import { Avatar } from '~/ui/Avatar';
import { Button } from '~/ui/Button';
import { Card } from '~/ui/Card';
import { Chips } from '~/ui/Chips';
import { useConfirm } from '~/ui/Confirm';
import { Dialog } from '~/ui/Dialog';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Input } from '~/ui/Input';
import { PasswordInput } from '~/ui/PasswordInput';
import { Notice } from '~/ui/Notice';
import { PageHeader } from '~/ui/PageHeader';
import { Pill } from '~/ui/Pill';
import { PersonSelect } from '~/ui/PersonSelect';
import { Skeleton } from '~/ui/Skeleton';
import { Switch } from '~/ui/Switch';
import { STATUS_TONE } from '~/ui/tones';
import { type SectionMeta, SettingsNav, SettingsSearch } from '~/features/settings/SettingsNav';
import { hasSettingsSection } from '~/features/settings/settingsIndex';
import { Hint } from '~/ui/Hint';

const SECTIONS = [
  'profile',
  'appearance',
  'notifications',
  'devices',
  'apps',
  'workspace',
  'sites',
  'attendance',
  'roles',
  'claude',
  'announce',
] as const;
export type SettingsSection = (typeof SECTIONS)[number];

export const Route = createFileRoute('/_app/settings')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { section: SettingsSection; find?: string; google?: string } => ({
    section: (SECTIONS as readonly string[]).includes(String(search['section']))
      ? (search['section'] as SettingsSection)
      : 'profile',
    // The card a search result points at, scrolled to on arrival.
    ...(typeof search['find'] === 'string' && search['find'] ? { find: search['find'] } : {}),
    // What Google said when it sent the person back from connecting.
    ...(typeof search['google'] === 'string' ? { google: search['google'] } : {}),
  }),
  component: SettingsPage,
});

const SECTION_META: Record<SettingsSection, SectionMeta> = {
  profile: { label: 'Profile', icon: User, group: 'you' },
  appearance: { label: 'Appearance', icon: Palette, group: 'you' },
  notifications: { label: 'Notifications', icon: Bell, group: 'you' },
  devices: { label: 'Devices', icon: Smartphone, group: 'you' },
  apps: { label: 'Connected apps', icon: Plug, group: 'you' },
  workspace: { label: 'Workspace', icon: Building2, group: 'company' },
  sites: { label: 'Office sites', icon: MapPin, group: 'company' },
  attendance: { label: 'Attendance', icon: CalendarCheck, group: 'company' },
  roles: { label: 'Order roles', icon: ShieldCheck, group: 'company' },
  claude: { label: 'Claude (AI)', icon: Sparkles, group: 'company' },
  announce: { label: 'Send notification', icon: Megaphone, group: 'company' },
};

/** Scrolls to the card titled `find` once the section is on screen, and marks it a moment. */
function useScrollToCard(section: SettingsSection, find: string | undefined) {
  useEffect(() => {
    if (!find) return;
    let left = 20;
    let timer = 0;
    // The section may still be loading its data: look again for a couple of seconds.
    const look = () => {
      const heading = [...document.querySelectorAll('main section h2')].find(
        (h) => h.textContent?.trim() === find,
      );
      const card = heading?.closest('section');
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'start' });
        card.classList.add('ring-2', 'ring-accent');
        timer = window.setTimeout(() => card.classList.remove('ring-2', 'ring-accent'), 1800);
      } else if (--left > 0) {
        timer = window.setTimeout(look, 100);
      }
    };
    timer = window.setTimeout(look, 50);
    return () => window.clearTimeout(timer);
  }, [section, find]);
}

function SettingsPage() {
  const me = useMe();
  const { section, find, google } = Route.useSearch();
  const navigate = useNavigate();
  const isOwner = me.person.role === 'owner';
  const isManager = me.person.role === 'manager';
  const sections = SECTIONS.filter((s) => hasSettingsSection(me.person.role, s));
  const current: SettingsSection = sections.includes(section) ? section : 'profile';
  useScrollToCard(current, find);

  return (
    <div className="mx-auto max-w-[1100px]">
      <PageHeader
        eyebrow="Settings"
        title="Settings"
        subtitle={
          me.person.role === 'guest'
            ? 'Your profile and password, notifications, how the app looks, and your devices.'
            : "Your profile, how the app looks, and the company's defaults."
        }
      />

      <div className="mb-4 flex flex-col gap-3 lg:hidden">
        <SettingsSearch sections={sections} meta={SECTION_META} />
        <Chips
          label="Settings section"
          options={sections.map((s) => ({ value: s, label: SECTION_META[s].label }))}
          value={current}
          onChange={(s) => void navigate({ to: '/settings', search: { section: s } })}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="hidden lg:sticky lg:top-4 lg:block lg:self-start">
          <SettingsSearch sections={sections} meta={SECTION_META} className="mb-4" />
          <SettingsNav sections={sections} meta={SECTION_META} current={current} />
        </aside>

        <div key={current} className="animate-fade">
          {current === 'profile' && <ProfileSection />}
          {current === 'appearance' && <AppearanceSection />}
          {current === 'notifications' && <NotificationsSettings />}
          {current === 'announce' && (isOwner || isManager) && <SendNotification />}
          {current === 'workspace' && isOwner && <WorkspaceSection />}
          {current === 'roles' && isOwner && (
            <Card title="Order roles" eyebrow="Order Tracker">
              <CompanyRoles />
            </Card>
          )}
          {current === 'sites' && isOwner && <OfficeSitesSection />}
          {current === 'attendance' && isOwner && <AttendanceSection />}
          {current === 'claude' && isOwner && <ClaudeSection />}
          {current === 'apps' && <ConnectedApps result={google} />}
          {current === 'devices' && <DevicesSection />}
        </div>
      </div>
    </div>
  );
}

// ---- profile

const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** Your picture, with Change and Remove: a photo from this device, shown to the company. */
function ProfilePhoto({
  personId,
  name,
  photoId,
}: {
  personId: string;
  name: string;
  photoId: string | null;
}) {
  const set = useSetAvatar(personId);
  const remove = useRemoveAvatar(personId);
  const inputId = useId();
  // A chosen photo is framed first; only the framed square is uploaded.
  const [picked, setPicked] = useState<File | null>(null);
  return (
    <div className="flex items-center gap-3">
      <Hint label="Change photo">
        <label htmlFor={inputId} className="group relative cursor-pointer">
          <Avatar name={name} size="lg" personId={personId} photoId={photoId} />
          <span className="absolute inset-0 grid place-items-center rounded-full bg-ink/50 text-[11px] font-medium text-paper opacity-0 transition-opacity group-hover:opacity-100">
            {set.isPending ? '…' : 'Change'}
          </span>
        </label>
      </Hint>
      <input
        id={inputId}
        type="file"
        accept={AVATAR_TYPES.join(',')}
        className="sr-only"
        onChange={(e) => {
          const photo = e.target.files?.[0];
          e.target.value = '';
          if (!photo) return;
          if (!AVATAR_TYPES.includes(photo.type)) {
            notify.error('Choose a photo: JPG, PNG or WebP.');
            return;
          }
          setPicked(photo);
        }}
      />
      {picked && (
        <AvatarCropper
          file={picked}
          saving={set.isPending}
          onCancel={() => setPicked(null)}
          onUnreadable={() => {
            setPicked(null);
            notify.error('That photo could not be opened. Try another.');
          }}
          onSave={(cropped) =>
            set.mutate(cropped, {
              onSuccess: () => {
                setPicked(null);
                notify.success('Photo updated.');
              },
              onError: (err) => notify.error('Could not change the photo.', err),
            })
          }
        />
      )}
      <div className="flex flex-col items-start gap-1">
        <label
          htmlFor={inputId}
          className="cursor-pointer text-sm font-medium text-accent hover:underline"
        >
          {photoId ? 'Change photo' : 'Add a photo'}
        </label>
        {photoId && (
          <button
            type="button"
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(undefined, {
                onSuccess: () => notify.success('Photo removed.'),
                onError: (err) => notify.error('Could not remove the photo.', err),
              })
            }
            className="text-xs text-ink-3 hover:text-late hover:underline"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

function ProfileSection() {
  const me = useMe();
  const logout = useLogout();
  const p = me.person;
  const tz = p.tz ?? me.workspace.tz;
  return (
    <Card
      title={p.name}
      eyebrow="You"
      dataTour="set-profile"
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={() => logout.mutate()}
          loading={logout.isPending}
        >
          Sign out
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <ProfilePhoto personId={p.id} name={p.name} photoId={p.avatarFileId} />
        <div className="flex flex-wrap gap-2">
          <Pill>{ROLE_LABEL[p.role]}</Pill>
          <Pill tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</Pill>
          {p.title && <Pill>{p.title}</Pill>}
        </div>
      </div>
      <dl className="grid grid-cols-[112px_minmax(0,1fr)] gap-y-2 text-sm md:grid-cols-[140px_minmax(0,1fr)]">
        <dt className="text-ink-3">Email</dt>
        <dd className="font-mono text-xs leading-5 break-all">{p.email}</dd>
        {/* Someone from outside the company has no employee code, schedule or attendance. */}
        {p.role !== 'guest' && (
          <>
            <dt className="text-ink-3">Employee code</dt>
            <dd className="font-mono">{p.empCode ?? '—'}</dd>
            <dt className="text-ink-3">Working week</dt>
            <dd>{describeSchedule(p.schedule ?? me.workspace.defaultSchedule)}</dd>
          </>
        )}
        <dt className="text-ink-3">Time zone</dt>
        <dd>{tz}</dd>
        {p.role !== 'guest' && (
          <>
            <dt className="text-ink-3">Attendance</dt>
            <dd>{p.clockRequired ? 'Expected to punch in' : 'Not expected to punch in'}</dd>
          </>
        )}
      </dl>
      {p.role !== 'guest' && (
        <p className="mt-4 text-xs text-ink-3">
          Schedule and time zone are set by the company. Ask your manager to change them.
        </p>
      )}
      <div className="mt-4 border-t border-line pt-4" data-tour="set-password">
        <ChangePasswordForm />
      </div>
    </Card>
  );
}

function ChangePasswordForm() {
  const change = useChangePassword();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const { fields: fieldErrors, message: formMessage } = formError(change.error);

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
    change.reset();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== confirm) {
      notify.error('The two new passwords do not match.');
      return;
    }
    try {
      const res = await change.mutateAsync({ currentPassword: current, newPassword: next });
      const n = res.otherSessionsSignedOut;
      notify.success(
        'Password changed.',
        n > 0 ? `${n} other device${n === 1 ? '' : 's'} signed out.` : undefined,
      );
      reset();
      setOpen(false);
    } catch {
      // shown inline
    }
  };

  if (!open) {
    return (
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Change password
      </Button>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex max-w-sm flex-col gap-3" noValidate>
      <PasswordInput
        label="Current password"
        autoComplete="current-password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        error={fieldErrors['currentPassword']}
        required
      />
      <PasswordInput
        label="New password"
        autoComplete="new-password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        error={fieldErrors['newPassword']}
        hint="At least 10 characters."
        required
      />
      <PasswordInput
        label="Confirm new password"
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        required
      />
      {formMessage && <Notice tone="late">{formMessage}</Notice>}
      <div className="flex gap-2">
        <Button type="submit" size="sm" loading={change.isPending}>
          Update password
        </Button>
        <Button
          type="button"
          variant="quiet"
          size="sm"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
      <p className="text-xs text-ink-3">Changing it signs you out on your other devices.</p>
    </form>
  );
}

// ---- appearance

const THEMES: { value: Theme; label: string; icon: LucideIcon }[] = [
  { value: 'system', label: 'Match the system', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

function AppearanceSection() {
  const me = useMe();
  const [theme, setTheme] = useTheme();
  const [weekStart, setWeekStart] = useWeekStart();
  const skew = clockSkewSeconds();

  return (
    <div className="flex flex-col gap-4">
      <Card title="Appearance" eyebrow="This device" dataTour="set-appearance">
        <p className="mb-5 text-sm text-ink-2">
          Light or dark, and the colours of the whole app. These choices are remembered on this
          device only.
        </p>

        <fieldset className="mb-7">
          <legend className="mb-2 text-sm font-medium">Mode</legend>
          <div className="flex flex-wrap gap-2">
            {THEMES.map((t) => {
              const on = theme === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setTheme(t.value)}
                  className={`inline-flex h-10 items-center gap-2 rounded-control border px-3 text-sm ${
                    on
                      ? 'border-accent bg-accent-soft text-accent-ink'
                      : 'border-line text-ink-2 hover:bg-paper-2'
                  }`}
                >
                  <t.icon size={16} aria-hidden />
                  {t.label}
                </button>
              );
            })}
          </div>
          {theme === 'system' && (
            <p className="mt-2 text-xs text-ink-3">
              Your system is asking for {systemTheme()} right now.
            </p>
          )}
        </fieldset>

        <ThemeEditor />
      </Card>

      <Card title="Calendar" eyebrow="This device">
        <fieldset>
          <legend className="mb-2 text-sm font-medium">Week starts on</legend>
          <Chips
            label="Week starts on"
            options={[
              { value: 'monday', label: 'Monday' },
              { value: 'sunday', label: 'Sunday' },
            ]}
            value={weekStart}
            onChange={setWeekStart}
          />
        </fieldset>
      </Card>

      {me.person.role !== 'guest' && (
        <Card title="Chats" eyebrow="Every device">
          <p className="mb-5 text-sm text-ink-2">
            How your chats look to you: a wallpaper, rows or bubbles, and the text size. Nobody else
            sees it. A chat can have a look of its own from the palette in its header.
          </p>
          <ChatLookSettings />
        </Card>
      )}

      <Card title="Clock" eyebrow="Server time">
        <p className="text-sm text-ink-2">
          This device's clock is{' '}
          {Math.abs(skew) < 2
            ? 'in step with'
            : `${Math.abs(skew)} s ${skew > 0 ? 'ahead of' : 'behind'}`}{' '}
          the server. Every time on screen comes from the server.
        </p>
      </Card>
    </div>
  );
}

// ---- workspace (owner)

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function WorkspaceSection() {
  const settings = useQuery(settingsQueryOptions);
  return settings.isPending ? (
    <Skeleton lines={8} />
  ) : settings.isError ? (
    <ErrorNotice error={settings.error} onRetry={() => settings.refetch()} />
  ) : (
    <div className="flex flex-col gap-4">
      <SettingsForm key={settings.data.updatedAt} initial={settings.data} />
      <HolidaysPanel />
    </div>
  );
}

type Form = Required<
  Pick<
    UpdateSettingsBody,
    | 'name'
    | 'tz'
    | 'hourlyEnabled'
    | 'hourlyIntervalMin'
    | 'hourlySnoozeMin'
    | 'skewThresholdSec'
    | 'budgetTolerancePct'
    | 'workdayMinutes'
    | 'attendanceGraceMin'
    | 'boardArchiveDays'
    | 'reportsEnabled'
    | 'clockRemindersEnabled'
    | 'reportReminderTimes'
    | 'reportLateWindowDays'
  >
> & {
  days: number[];
  start: string;
  end: string;
  /** '' for no form. */
  reportFormUrl: string;
};

function SettingsForm({ initial }: { initial: WorkspaceView }) {
  const toForm = (s: WorkspaceView): Form => ({
    name: s.name,
    tz: s.tz,
    hourlyEnabled: s.hourlyEnabled,
    hourlyIntervalMin: s.hourlyIntervalMin,
    hourlySnoozeMin: s.hourlySnoozeMin,
    skewThresholdSec: s.skewThresholdSec,
    budgetTolerancePct: s.budgetTolerancePct,
    workdayMinutes: s.workdayMinutes,
    attendanceGraceMin: s.attendanceGraceMin,
    boardArchiveDays: s.boardArchiveDays,
    reportsEnabled: s.reportsEnabled,
    clockRemindersEnabled: s.clockRemindersEnabled,
    reportReminderTimes: s.reportReminderTimes,
    reportLateWindowDays: s.reportLateWindowDays,
    reportFormUrl: s.reportFormUrl ?? '',
    days: s.defaultSchedule.days,
    start: s.defaultSchedule.start,
    end: s.defaultSchedule.end,
  });
  // The parent remounts this form (key = updatedAt) whenever fresh settings arrive.
  const [form, setForm] = useState<Form>(() => toForm(initial));
  const update = useUpdateSettings();
  const { fields: fieldErrors, message: formMessage } = formError(update.error);
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(initial));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const body: UpdateSettingsBody = {};
    if (form.name !== initial.name) body.name = form.name;
    if (form.tz !== initial.tz) body.tz = form.tz;
    if (form.hourlyEnabled !== initial.hourlyEnabled) body.hourlyEnabled = form.hourlyEnabled;
    if (form.hourlyIntervalMin !== initial.hourlyIntervalMin)
      body.hourlyIntervalMin = form.hourlyIntervalMin;
    if (form.hourlySnoozeMin !== initial.hourlySnoozeMin)
      body.hourlySnoozeMin = form.hourlySnoozeMin;
    if (form.skewThresholdSec !== initial.skewThresholdSec)
      body.skewThresholdSec = form.skewThresholdSec;
    if (form.budgetTolerancePct !== initial.budgetTolerancePct)
      body.budgetTolerancePct = form.budgetTolerancePct;
    if (form.workdayMinutes !== initial.workdayMinutes) body.workdayMinutes = form.workdayMinutes;
    if (form.attendanceGraceMin !== initial.attendanceGraceMin)
      body.attendanceGraceMin = form.attendanceGraceMin;
    if (form.boardArchiveDays !== initial.boardArchiveDays)
      body.boardArchiveDays = form.boardArchiveDays;
    if (form.reportsEnabled !== initial.reportsEnabled) body.reportsEnabled = form.reportsEnabled;
    if (form.clockRemindersEnabled !== initial.clockRemindersEnabled)
      body.clockRemindersEnabled = form.clockRemindersEnabled;
    const times = form.reportReminderTimes.filter(Boolean);
    if (JSON.stringify(times) !== JSON.stringify(initial.reportReminderTimes))
      body.reportReminderTimes = times;
    if (form.reportLateWindowDays !== initial.reportLateWindowDays)
      body.reportLateWindowDays = form.reportLateWindowDays;
    if (form.reportFormUrl.trim() !== (initial.reportFormUrl ?? ''))
      body.reportFormUrl = form.reportFormUrl.trim() || null;
    const sched = { days: [...form.days].sort((a, b) => a - b), start: form.start, end: form.end };
    const was = {
      ...initial.defaultSchedule,
      days: [...initial.defaultSchedule.days].sort((a, b) => a - b),
    };
    if (JSON.stringify(sched) !== JSON.stringify(was)) body.defaultSchedule = sched;
    try {
      await update.mutateAsync(body);
      notify.success('Settings saved.');
    } catch {
      // shown inline
    }
  };

  const toggleDay = (d: number) =>
    setForm((f) => ({
      ...f,
      days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d],
    }));

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate data-tour="set-defaults">
      <Card
        title="Company"
        eyebrow="Workspace"
        actions={
          <Button type="submit" size="sm" disabled={!dirty} loading={update.isPending}>
            Save changes
          </Button>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={fieldErrors['name']}
          />
          <Input
            label="Time zone"
            mono
            value={form.tz}
            onChange={(e) => setForm({ ...form, tz: e.target.value })}
            error={fieldErrors['tz']}
            hint="An IANA name, for example Asia/Kolkata."
          />
        </div>
      </Card>

      <Card title="Default working week" eyebrow="Schedule">
        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium">Working days</legend>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((label, i) => {
              const d = i + 1;
              const on = form.days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleDay(d)}
                  className={`h-9 rounded-control border px-3 text-sm ${
                    on
                      ? 'border-accent bg-accent-soft text-accent-ink'
                      : 'border-line text-ink-2 hover:bg-paper-2'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {fieldErrors['defaultSchedule.days'] && (
            <p className="mt-1 text-xs text-late">{fieldErrors['defaultSchedule.days']}</p>
          )}
        </fieldset>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Day starts"
            type="time"
            mono
            value={form.start}
            onChange={(e) => setForm({ ...form, start: e.target.value })}
            error={fieldErrors['defaultSchedule.start']}
          />
          <Input
            label="Day ends"
            type="time"
            mono
            value={form.end}
            onChange={(e) => setForm({ ...form, end: e.target.value })}
            error={fieldErrors['defaultSchedule.end']}
          />
        </div>
      </Card>

      <Card title="Duties and attendance" eyebrow="Rules">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Delay after (% over budget)"
            type="number"
            min={0}
            max={200}
            mono
            value={form.budgetTolerancePct}
            onChange={(e) => setForm({ ...form, budgetTolerancePct: Number(e.target.value) })}
            error={fieldErrors['budgetTolerancePct']}
            hint="A run this far over its budget needs a reason."
          />
          <Input
            label="Working day (hours)"
            type="number"
            min={1}
            max={12}
            step={0.5}
            mono
            value={form.workdayMinutes / 60}
            onChange={(e) =>
              setForm({ ...form, workdayMinutes: Math.round(Number(e.target.value) * 60) })
            }
            error={fieldErrors['workdayMinutes']}
            hint="What '1 day' of effort means on a task. A task can take up to 10 such days."
          />
          <Input
            label="Late after (minutes)"
            type="number"
            min={0}
            max={180}
            mono
            value={form.attendanceGraceMin}
            onChange={(e) => setForm({ ...form, attendanceGraceMin: Number(e.target.value) })}
            error={fieldErrors['attendanceGraceMin']}
            hint="Punching in this long after the day starts still counts as on time."
          />
        </div>
      </Card>

      <Card title="Hourly report and clock" eyebrow="Rhythm">
        <Switch
          label="Ask for an hourly check-in"
          hint="When on, people who are working are asked what they did each hour, with the tasks their timer ran ticked for them. Never on a break or once clocked out. Managers see who answered on the Work clock's Team today."
          checked={form.hourlyEnabled}
          onChange={(v) => setForm({ ...form, hourlyEnabled: v })}
        />
        <div className="mt-4">
          <Switch
            label="Remind people who have not clocked in"
            hint="On a working day, someone who has not clocked in is reminded when they become late (start plus the grace minutes), and again an hour later. Not on days off or holidays, and not for people who are not expected to clock in."
            checked={form.clockRemindersEnabled}
            onChange={(v) => setForm({ ...form, clockRemindersEnabled: v })}
          />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Input
            label="Ask every (minutes)"
            type="number"
            min={15}
            max={480}
            mono
            value={form.hourlyIntervalMin}
            onChange={(e) => setForm({ ...form, hourlyIntervalMin: Number(e.target.value) })}
            error={fieldErrors['hourlyIntervalMin']}
          />
          <Input
            label="Snooze for (minutes)"
            type="number"
            min={1}
            max={120}
            mono
            value={form.hourlySnoozeMin}
            onChange={(e) => setForm({ ...form, hourlySnoozeMin: Number(e.target.value) })}
            error={fieldErrors['hourlySnoozeMin']}
          />
          <Input
            label="Clock skew threshold (s)"
            type="number"
            min={10}
            max={3600}
            mono
            value={form.skewThresholdSec}
            onChange={(e) => setForm({ ...form, skewThresholdSec: Number(e.target.value) })}
            error={fieldErrors['skewThresholdSec']}
            hint="Beyond this, a device clock that disagrees with the server is flagged to managers."
          />
          <Input
            label="Archive finished tasks after (days)"
            type="number"
            min={0}
            max={90}
            mono
            value={form.boardArchiveDays}
            onChange={(e) => setForm({ ...form, boardArchiveDays: Number(e.target.value) })}
            error={fieldErrors['boardArchiveDays']}
            hint="Keeps the board light. Archived work stays searchable under Tasks › Archive. 0 never archives."
          />
        </div>
      </Card>
      <Card title="Daily reports" eyebrow="End of day" dataTour="set-reports">
        <div className="flex flex-col gap-4">
          <Switch
            label="Remind people and managers"
            hint="When on, people who have not sent today's report are reminded at the times below, and managers get yesterday's missing reports each morning."
            checked={form.reportsEnabled}
            onChange={(v) => setForm({ ...form, reportsEnabled: v })}
          />
          <fieldset disabled={!form.reportsEnabled} className="disabled:opacity-60">
            <legend className="mb-2 text-sm font-medium">Reminder times</legend>
            <div className="flex flex-wrap items-center gap-2">
              {form.reportReminderTimes.map((t, i) => (
                <div key={i} className="flex items-center gap-1">
                  <input
                    type="time"
                    aria-label={`Reminder ${i + 1}`}
                    value={t}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        reportReminderTimes: form.reportReminderTimes.map((x, j) =>
                          j === i ? e.target.value : x,
                        ),
                      })
                    }
                    className="h-9 rounded-control border border-line bg-paper px-2 font-mono text-sm tabular focus:border-accent"
                  />
                  <button
                    type="button"
                    aria-label={`Remove reminder ${i + 1}`}
                    onClick={() =>
                      setForm({
                        ...form,
                        reportReminderTimes: form.reportReminderTimes.filter((_, j) => j !== i),
                      })
                    }
                    className="grid size-8 place-items-center rounded-control text-ink-3 hover:bg-late-soft hover:text-late"
                  >
                    <Trash2 size={14} aria-hidden />
                  </button>
                </div>
              ))}
              {form.reportReminderTimes.length < 4 && (
                <Button
                  type="button"
                  variant="quiet"
                  size="sm"
                  icon={<Plus size={14} aria-hidden />}
                  onClick={() =>
                    setForm({
                      ...form,
                      reportReminderTimes: [...form.reportReminderTimes, '22:00'],
                    })
                  }
                >
                  Add a time
                </Button>
              )}
            </div>
            <p className="mt-1.5 text-xs text-ink-3">
              In each person's own time zone. A report is open until midnight.
              {fieldErrors['reportReminderTimes'] && (
                <span className="ml-1 text-late">{fieldErrors['reportReminderTimes']}</span>
              )}
            </p>
          </fieldset>
          <div className="sm:w-1/3">
            <Input
              label="Late reports allowed for (days)"
              type="number"
              min={1}
              max={31}
              mono
              value={form.reportLateWindowDays}
              onChange={(e) => setForm({ ...form, reportLateWindowDays: Number(e.target.value) })}
              error={fieldErrors['reportLateWindowDays']}
              hint="How far back someone may still send a report, for their manager to approve."
            />
          </div>
          <div>
            <Input
              label="Google Form for reports (optional)"
              type="url"
              inputMode="url"
              value={form.reportFormUrl}
              onChange={(e) => setForm({ ...form, reportFormUrl: e.target.value })}
              placeholder="https://docs.google.com/forms/d/e/…/viewform?usp=pp_url&entry.…={report}"
              error={fieldErrors['reportFormUrl']}
              hint="Each sent report gets a button that opens this form already filled in, for the person to submit. In Google Forms, open ⋮ › Get pre-filled link, type {report} in the report answer ({name} and {date} too, if the form asks), then Get link and paste it here. Leave it empty for no form."
            />
          </div>
        </div>
      </Card>
      {formMessage && <Notice tone="late">{formMessage}</Notice>}
    </form>
  );
}

function HolidaysPanel() {
  const holidays = useQuery(holidaysQueryOptions());
  const create = useCreateHoliday();
  const remove = useDeleteHoliday();
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await create.mutateAsync({ date, name: name.trim() });
      setAdding(false);
      setDate('');
      setName('');
    } catch {
      // shown inline
    }
  };

  const { fields: fieldErrors, message: formMessage } = formError(create.error);

  // Unique per dialog: a page may hold several (one per card), all in the DOM.
  const addHolidayId = useId();
  return (
    <Card
      title="Holidays"
      eyebrow="Calendar"
      actions={
        <Button
          variant="secondary"
          size="sm"
          icon={<Plus size={14} aria-hidden />}
          onClick={() => setAdding(true)}
        >
          Add a holiday
        </Button>
      }
    >
      {holidays.isPending ? (
        <Skeleton lines={3} />
      ) : holidays.isError ? (
        <ErrorNotice error={holidays.error} onRetry={() => holidays.refetch()} />
      ) : holidays.data.length === 0 ? (
        <p className="text-sm text-ink-3">
          No holidays yet. Repeating duties skip holidays when asked to.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {holidays.data.map((h) => (
            <li key={h.id} className="flex items-center gap-3 py-2 text-sm">
              <span className="font-mono text-xs text-ink-2 tabular">{h.date}</span>
              <span className="flex-1">{h.name}</span>
              <button
                type="button"
                aria-label={`Remove ${h.name}`}
                onClick={async () => {
                  try {
                    await remove.mutateAsync(h.id);
                  } catch (err) {
                    notify.error('Could not remove the holiday.', err);
                  }
                }}
                className="grid size-7 place-items-center rounded-control text-ink-3 hover:bg-late-soft hover:text-late"
              >
                <Trash2 size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="Which day is a holiday?"
        size="sm"
        footer={
          <>
            <Button variant="quiet" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button type="submit" form={addHolidayId} loading={create.isPending}>
              Add
            </Button>
          </>
        }
      >
        <form id={addHolidayId} onSubmit={onAdd} className="flex flex-col gap-4" noValidate>
          <DatePicker label="Date" value={date} onChange={setDate} error={fieldErrors['date']} />
          <Input
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors['name']}
          />
          {formMessage && <Notice tone="late">{formMessage}</Notice>}
        </form>
      </Dialog>
    </Card>
  );
}

// ---- attendance (owner)

function AttendanceSection() {
  const importFile = useImportAttendance();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const importMessage = inlineError(importFile.error);

  const onPick = async (picked: File | null) => {
    setFile(picked);
    setPreview(null);
    importFile.reset();
    if (!picked) return;
    try {
      setPreview(await importFile.mutateAsync({ file: picked, dryRun: true }));
    } catch {
      // shown inline
    }
  };

  const onImport = async () => {
    if (!file) return;
    try {
      const result = await importFile.mutateAsync({ file, dryRun: false });
      setPreview(result);
      notify.success(
        `Imported ${result.dayCount} day${result.dayCount === 1 ? '' : 's'} of attendance.`,
      );
    } catch {
      // shown inline
    }
  };

  return (
    <div className="flex flex-col gap-6" data-tour="set-attendance">
      <Card title="Attendance import" eyebrow="Workspace">
        <p className="mb-4 text-sm text-ink-2">
          Export the Attendance Master report from Attendo, then bring the file in here. No paid API
          is needed.
        </p>
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-panel border border-dashed border-line bg-paper-2/40 px-4 py-8 text-center hover:border-accent">
          <FileUp size={24} aria-hidden className="text-ink-3" />
          <span className="text-sm font-medium">
            {file ? file.name : 'Choose an exported .xlsx file'}
          </span>
          <span className="text-xs text-ink-3">
            One row per person per day, with punch in and punch out.
          </span>
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="sr-only"
            onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
          />
        </label>

        {importFile.isPending && !preview && (
          <p className="mt-4 text-sm text-ink-3">Reading the file…</p>
        )}
        {importMessage && (
          <div className="mt-4">
            <Notice tone="late">{importMessage}</Notice>
          </div>
        )}

        {preview && (
          <div className="mt-5 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3 @md:grid-cols-4">
              <Stat label="Dates" value={preview.dateRange ? String(preview.dateCount) : '0'} />
              <Stat label="People in file" value={String(preview.employeeCount)} />
              <Stat label="Matched" value={String(preview.matchedEmployees)} />
              <Stat
                label={preview.dryRun ? 'Days to build' : 'Days built'}
                value={String(preview.dayCount)}
              />
            </div>

            {preview.dateRange && (
              <p className="text-sm text-ink-2">
                {preview.dateRange.from} to {preview.dateRange.to}, {preview.recordCount} rows,{' '}
                {preview.punchCount} punches.
              </p>
            )}

            {preview.unmapped.length > 0 && (
              <Notice tone="warn">
                <p className="mb-1 font-medium">
                  {preview.unmapped.length} employee code{preview.unmapped.length === 1 ? '' : 's'}{' '}
                  with no person
                </p>
                <p className="mb-2">
                  These rows are stored but build no attendance day until someone carries the code.
                  Set the employee code on each person's profile.
                </p>
                <ul className="flex flex-col gap-1">
                  {preview.unmapped.slice(0, 20).map((u) => (
                    <li key={u.empCode} className="font-mono tabular">
                      {u.empCode} · {u.name} · {u.days} day{u.days === 1 ? '' : 's'}
                    </li>
                  ))}
                  {preview.unmapped.length > 20 && (
                    <li className="opacity-70">…and {preview.unmapped.length - 20} more.</li>
                  )}
                </ul>
              </Notice>
            )}

            {preview.rowErrors.length > 0 && (
              <Notice tone="warn">
                <p className="mb-1 font-medium">
                  {preview.rowErrors.length + preview.truncatedErrors} row
                  {preview.rowErrors.length + preview.truncatedErrors === 1 ? '' : 's'} skipped
                </p>
                <ul className="flex flex-col gap-1">
                  {preview.rowErrors.slice(0, 8).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                  {preview.rowErrors.length > 8 && <li className="opacity-70">…and more.</li>}
                </ul>
              </Notice>
            )}

            {preview.dryRun ? (
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  icon={<Upload size={16} aria-hidden />}
                  loading={importFile.isPending}
                  disabled={preview.matchedEmployees === 0 && preview.unmapped.length === 0}
                  onClick={() => void onImport()}
                >
                  Import {preview.dateCount} day{preview.dateCount === 1 ? '' : 's'}
                </Button>
                <span className="text-xs text-ink-3">
                  Nothing is saved until you import. Re-importing a month replaces it.
                </span>
              </div>
            ) : (
              <Notice tone="done">
                <p className="mb-1 font-medium">Imported</p>
                Attendance for {preview.dateRange?.from} to {preview.dateRange?.to} is in. Re-import
                the same file any time to refresh it.
              </Notice>
            )}
          </div>
        )}
      </Card>

      <Card title="Imported coverage" eyebrow="Workspace">
        <p className="mb-4 text-sm text-ink-2">
          Which dates attendance has been brought in for. Import a month, then check it is all green
          here.
        </p>
        <CoverageCalendar />
      </Card>

      <LinkEmployeesCard />
    </div>
  );
}

/**
 * Attendo employee codes seen in the punches that no person carries yet, each with a
 * picker of people to link to. Linking sets the code on that person and builds their days.
 */
function LinkEmployeesCard() {
  const unmapped = useQuery(unmappedQueryOptions);
  const people = useQuery(peopleQueryOptions({ status: 'active' }));
  const link = useLinkEmployee();
  const remove = useRemoveUnmapped();
  // `confirm` below is the replace-code dialog's state, so the shared dialog is `ask`.
  const ask = useConfirm();

  const onRemove = async (empCode: string, name: string, punches: number) => {
    if (
      !(await ask({
        title: `Remove ${name} (code ${empCode}) and their ${punches} imported punch${punches === 1 ? '' : 'es'}?`,
        body: 'This is for someone who has left. It cannot be undone, but re-importing a file brings the data back.',
        confirmLabel: 'Remove',
        tone: 'danger',
      }))
    )
      return;
    try {
      await remove.mutateAsync(empCode);
      notify.success(`Removed ${empCode}.`);
    } catch (e) {
      notify.error('Could not remove.', e);
    }
  };
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [unmappedQ, setUnmappedQ] = useState('');
  const uneedle = unmappedQ.trim().toLowerCase();
  const shownUnmapped = (unmapped.data ?? []).filter(
    (u) =>
      !uneedle ||
      u.empCode.toLowerCase().includes(uneedle) ||
      u.name.toLowerCase().includes(uneedle),
  );
  // Set when the chosen person already carries a different code: confirm before replacing.
  const [confirm, setConfirm] = useState<{
    empCode: string;
    personId: string;
    personName: string;
    currentCode: string;
  } | null>(null);

  const runLink = async (empCode: string, personId: string, replace: boolean) => {
    setBusy(empCode);
    try {
      const res = await link.mutateAsync({ empCode, personId, replace });
      notify.success(
        `Linked ${empCode}: ${res.daysLinked} day${res.daysLinked === 1 ? '' : 's'} built.`,
      );
      setChoice((c) => {
        const next = { ...c };
        delete next[empCode];
        return next;
      });
    } catch (e) {
      notify.error('Could not link this employee.', e);
    } finally {
      setBusy(null);
    }
  };

  const onLink = (empCode: string) => {
    const personId = choice[empCode];
    if (!personId) return;
    const person = (people.data ?? []).find((p) => p.id === personId);
    if (person?.empCode && person.empCode !== empCode) {
      setConfirm({ empCode, personId, personName: person.name, currentCode: person.empCode });
      return;
    }
    void runLink(empCode, personId, false);
  };

  return (
    <Card title="Link employees" eyebrow="Workspace">
      <p className="mb-4 text-sm text-ink-2">
        Match each Attendo employee to a person here. Linking sets their employee code and builds
        their attendance from the punches already imported.
      </p>

      {(unmapped.data?.length ?? 0) > 5 && (
        <div className="mb-3">
          <Input
            label="Search"
            type="search"
            placeholder="Search by code or name"
            value={unmappedQ}
            onChange={(e) => setUnmappedQ(e.target.value)}
          />
        </div>
      )}

      {unmapped.isLoading ? (
        <Skeleton lines={3} />
      ) : (unmapped.data?.length ?? 0) === 0 ? (
        <Notice tone="done">
          Every imported employee code is linked to a person. Import a file in the card above to see
          any new ones here.
        </Notice>
      ) : shownUnmapped.length === 0 ? (
        <Notice tone="neutral">Nobody matches that search.</Notice>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {shownUnmapped.map((u) => (
            <li key={u.empCode} className="flex flex-col gap-3 py-3 @md:flex-row @md:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono tabular text-sm font-medium">{u.empCode}</span>
                  <span className="truncate text-sm">{u.name}</span>
                </div>
                <div className="text-xs text-ink-3">
                  {u.punchCount} punches · {u.firstDate} to {u.lastDate}
                </div>
              </div>
              <div className="flex items-center gap-2 @md:w-[360px]">
                <PersonSelect
                  className="flex-1"
                  value={choice[u.empCode] ?? ''}
                  ariaLabel={`Person for ${u.name}`}
                  onChange={(id) => setChoice((c) => ({ ...c, [u.empCode]: id }))}
                  options={(people.data ?? []).map((p) => ({
                    id: p.id,
                    name: p.name,
                    hint: p.empCode ? `code ${p.empCode}` : undefined,
                  }))}
                />
                <Button
                  disabled={!choice[u.empCode]}
                  loading={busy === u.empCode}
                  onClick={() => onLink(u.empCode)}
                >
                  Link
                </Button>
                <button
                  type="button"
                  aria-label={`Remove ${u.name}`}
                  onClick={() => void onRemove(u.empCode, u.name, u.punchCount)}
                  className="grid size-10 shrink-0 place-items-center rounded-control border border-line text-ink-3 hover:bg-late-soft hover:text-late"
                >
                  <Trash2 size={16} aria-hidden />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title="Replace this employee code?"
        footer={
          confirm && (
            <>
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                loading={busy === confirm.empCode}
                onClick={() =>
                  void runLink(confirm.empCode, confirm.personId, true).then(() => setConfirm(null))
                }
              >
                Replace with {confirm.empCode}
              </Button>
            </>
          )
        }
      >
        {confirm && (
          <p className="text-sm text-ink-2">
            {confirm.personName} already carries employee code{' '}
            <span className="font-mono tabular">{confirm.currentCode}</span>. Replacing it with{' '}
            <span className="font-mono tabular">{confirm.empCode}</span> re-links their attendance
            to the imported punches.
          </p>
        )}
      </Dialog>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-control border border-line bg-paper-2/40 px-3 py-2">
      <div className="text-xs text-ink-3">{label}</div>
      <div className="font-mono text-xl tabular">{value}</div>
    </div>
  );
}

// ---- devices

function DevicesSection() {
  const me = useMe();
  const tz = me.person.tz ?? me.workspace.tz;
  const sessions = useQuery(sessionsQueryOptions);
  const revoke = useRevokeSession();

  return (
    <Card title="Devices" eyebrow="Signed in" dataTour="set-devices">
      {sessions.isPending ? (
        <Skeleton lines={3} />
      ) : sessions.isError ? (
        <ErrorNotice error={sessions.error} onRetry={() => sessions.refetch()} />
      ) : (
        <ul className="divide-y divide-line">
          {sessions.data.sessions.map((s) => {
            const Icon = s.kind === 'android' ? Smartphone : s.kind === 'desktop' ? Laptop : Globe;
            return (
              <li key={s.id} className="flex items-center gap-3 py-3 text-sm">
                <Icon size={18} aria-hidden className="text-ink-3" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">
                      {s.deviceName ?? (s.kind === 'web' ? 'Web browser' : s.kind)}
                    </span>
                    {s.current && <Pill tone="accent">This device</Pill>}
                  </div>
                  <div className="text-xs text-ink-3">
                    Last seen {relativeTime(s.lastSeenAt)} · signed in{' '}
                    {formatDateTime(s.createdAt, tz)}
                  </div>
                </div>
                {!s.current && (
                  <Button
                    variant="quiet"
                    size="sm"
                    loading={revoke.isPending && revoke.variables === s.id}
                    onClick={async () => {
                      try {
                        await revoke.mutateAsync(s.id);
                        notify.success('Signed out that device.');
                      } catch (err) {
                        notify.error('Could not sign out that device.', err);
                      }
                    }}
                  >
                    Sign out
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
